import React, { createContext, useContext, useState, useRef, useMemo, useEffect, useCallback } from "react";
import { calculateTrialDaysLeft } from "../utils/membershipEarnings";
import { messagesApi, notificationsApi } from "../api";
import { registerAlertHandlers } from "../utils/appAlert";
import { TRADING_ONBOARDING_ENABLED } from "../config/features";

const AppContext = createContext(null);

const PREVIEW_PRODUCTION_KEY = "outback.previewProductionMode";

// True whenever the app is running locally, either via `npm start` or a
// localhost build. Production deployments fail both checks.
const isDevEnvironment =
  process.env.NODE_ENV === "development" ||
  (typeof window !== "undefined" &&
    (window.location.hostname === "localhost" ||
      window.location.hostname === "127.0.0.1"));

const readStoredPreviewMode = () => {
  if (typeof window === "undefined") return false;
  try {
    return window.localStorage.getItem(PREVIEW_PRODUCTION_KEY) === "true";
  } catch {
    return false;
  }
};

export function AppProvider({ children }) {
  // ── Routing ────────────────────────────────────────────────────
  const [activeTab, setActiveTab] = useState("sign-in");

  // ── Auth / identity ──────────────────────────────────────────────
  const [user, setUser] = useState(null);
  const [isFirstTimeUser, setIsFirstTimeUser] = useState(false);
  const [isInTutorialFlow, setIsInTutorialFlow] = useState(false);
  const [showProfileTutorial, setShowProfileTutorial] = useState(false);
  const [profileTutorialKey] = useState(0);

  // ── Selected / detail state ──────────────────────────────────────
  const [selectedItem, setSelectedItem] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [deleteMessageCallback, setDeleteMessageCallback] = useState(null);
  const [selectedServiceProvider, setSelectedServiceProvider] = useState(null);

  // ── Sponsored ads ────────────────────────────────────────────────
  const [sponsoredAdData, setSponsoredAdData] = useState(null);
  const [sponsoredAdSource, setSponsoredAdSource] = useState(null);

  // ── Profile state ────────────────────────────────────────────────
  const [isProfileReadOnly, setIsProfileReadOnly] = useState(false);
  const [personalProfileComplete, setPersonalProfileComplete] = useState(false);
  const [tradingProfileComplete, setTradingProfileComplete] = useState(false);
  const [openToPreferences, setOpenToPreferences] = useState(["payment"]);
  const [profileRefreshKey, setProfileRefreshKey] = useState(0);

  // ── Favorites ────────────────────────────────────────────────────
  const [favorites, setFavorites] = useState([]);

  // ── Form / flow state ────────────────────────────────────────────
  const [addItemType, setAddItemType] = useState(null);
  const [editItemData, setEditItemData] = useState(null);
  const [successData, setSuccessData] = useState(null);
  const [feedbackInitialData, setFeedbackInitialData] = useState({});
  const [addAdvertisingKeyCounter, setAddAdvertisingKeyCounter] = useState(0);

  // ── Listing detail supplements ───────────────────────────────────
  const [locationsScreenData, setLocationsScreenData] = useState(null);
  const [advertisementsScreenData, setAdvertisementsScreenData] = useState(null);

  // ── Trading profile ──────────────────────────────────────────────
  const [tradingProfileInitialTab, setTradingProfileInitialTab] = useState("services");
  const [scrollToJobBoard, setScrollToJobBoard] = useState(false);
  const [scrollToServices, setScrollToServices] = useState(false);
  const [scrollToJobBoardSection, setScrollToJobBoardSection] = useState(false);
  const [jobsListRefreshKey, setJobsListRefreshKey] = useState(0);

  // ── Payment methods ──────────────────────────────────────────────
  const datePickerCallbackRef = useRef(null);
  const [selectedExpiryDate, setSelectedExpiryDate] = useState(null);
  const [paymentMethodView, setPaymentMethodView] = useState("selection");

  // ── Admin / pending ──────────────────────────────────────────────
  const [pendingItemsFilter, setPendingItemsFilter] = useState("all");
  const [pendingItemsData, setPendingItemsData] = useState([]);

  // ── UI chrome ────────────────────────────────────────────────────
  const [showTrialBanner, setShowTrialBanner] = useState(true);
  const [showAddMenu, setShowAddMenu] = useState(false);
  const [showHamburgerMenu, setShowHamburgerMenu] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  const [showPrivacy, setShowPrivacy] = useState(false);
  const [showSignOutModal, setShowSignOutModal] = useState(false);
  const [alertModal, setAlertModal] = useState({ visible: false, title: "", message: "" });

  // Alert.alert does nothing on React Native Web, so these route the app's
  // messages through AlertModal instead. showConfirm is the two-button form.
  // Screens reach these via src/utils/appAlert.js rather than this context.
  const showAlert = useCallback((title, message, options = {}) => {
    setAlertModal({
      visible: true,
      title,
      message,
      buttonText: options.buttonText || "OK",
      onDismiss: options.onDismiss || null,
    });
  }, []);

  const showConfirm = useCallback(({
    title,
    message,
    confirmText = "OK",
    cancelText = "Cancel",
    destructive = false,
    onConfirm,
  }) => {
    setAlertModal({
      visible: true,
      title,
      message,
      buttonText: confirmText,
      cancelText,
      destructive,
      onConfirm,
    });
  }, []);

  useEffect(() => {
    registerAlertHandlers({ showAlert, showConfirm });
  }, [showAlert, showConfirm]);
  const [previewProductionMode, setPreviewProductionMode] = useState(readStoredPreviewMode);

  // ── Notification / message counts ────────────────────────────────
  const [unreadNotifications, setUnreadNotifications] = useState(0);
  const [unreadMessages, setUnreadMessages] = useState(0);
  const [myListUnseenCount, setMyListUnseenCount] = useState(0);

  // Load unread notification count
  const loadUnreadNotificationCount = useCallback(async () => {
    if (!user) return;
    const result = await notificationsApi.countUnread();
    if (result.success) {
      setUnreadNotifications(result.count);
    }
  }, [user]);

  // Drop the badge on tap, then confirm the write. A refetch-before-write
  // loses the race and the 30s poll is what finally moves the number.
  const adjustUnreadNotificationCount = useCallback((delta) => {
    setUnreadNotifications((count) => Math.max(0, count + delta));
  }, []);

  // Load unread notifications on user change and poll every 30 seconds
  useEffect(() => {
    if (!user) {
      setUnreadNotifications(0);
      return;
    }
    loadUnreadNotificationCount();
    const interval = setInterval(loadUnreadNotificationCount, 30000);
    return () => clearInterval(interval);
  }, [user, loadUnreadNotificationCount]);

  // ── Filter caches (preserve across tab switches) ─────────────────
  const [servicesScreenFilters, setServicesScreenFilters] = useState({
    searchQuery: "", selectedCategories: [], selectedLocations: [], exchangeFilter: "all", smartMatchFilter: "all",
  });
  const [skillsNeededScreenFilters, setSkillsNeededScreenFilters] = useState({
    searchQuery: "", selectedCategories: [], selectedLocations: [], exchangeFilter: "all", smartMatchFilter: "all",
  });
  const [sponsoredListingsScreenFilters, setSponsoredListingsScreenFilters] = useState({
    searchQuery: "", selectedCategories: [], selectedLocations: [], exchangeFilter: "all", smartMatchFilter: "all",
  });
  const [cachedJobs, setCachedJobs] = useState(null);
  const [cachedSponsoredAds, setCachedSponsoredAds] = useState(null);
  const [cachedEvents, setCachedEvents] = useState(null);
  const [servicesScrollY, setServicesScrollY] = useState(0);

  // ── Derived / computed ───────────────────────────────────────────
  const trialDaysLeft = useMemo(() => calculateTrialDaysLeft(user), [user]);

  // Single source of truth for "should dev-only features be visible?".
  // Flipping the PROD pill makes a local build render the production surface.
  const showDevFeatures = isDevEnvironment && !previewProductionMode;

  const togglePreviewProductionMode = () => setPreviewProductionMode((prev) => !prev);

  // Survive a page refresh so the previewed surface stays put
  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      window.localStorage.setItem(PREVIEW_PRODUCTION_KEY, String(previewProductionMode));
    } catch {
      // storage unavailable (private mode) — preview simply won't persist
    }
  }, [previewProductionMode]);

  const shouldShowTrialBanner = false; // Hidden until paid tier is introduced

  const dismissTrialBanner = () => setShowTrialBanner(false);

  // ── Effects that only touch context state ────────────────────────

  // Auto-hide trial banner after 60 seconds
  useEffect(() => {
    if (!showTrialBanner) return;
    const timer = setTimeout(() => setShowTrialBanner(false), 60000);
    return () => clearTimeout(timer);
  }, [showTrialBanner]);

  // Reset banner on login
  useEffect(() => {
    if (user) setShowTrialBanner(true);
  }, [user]);

  // Close FAB menu whenever the active tab changes
  useEffect(() => {
    setShowAddMenu(false);
  }, [activeTab]);

  // Navigate to services when the required profiles are complete and onboarding is done
  useEffect(() => {
    const profilesComplete =
      personalProfileComplete &&
      (!TRADING_ONBOARDING_ENABLED || tradingProfileComplete);
    if (profilesComplete && !isFirstTimeUser && !isInTutorialFlow) {
      setActiveTab("services");
    }
  }, [personalProfileComplete, tradingProfileComplete, isFirstTimeUser, isInTutorialFlow]); // eslint-disable-line react-hooks/exhaustive-deps

  // Unread message count.
  //
  // Polled rather than pushed: a 30s delay on a new message is fine for this
  // app, and Realtime would add a websocket and reconnection handling for
  // seconds that nobody is waiting on.
  //
  // The lag that actually matters is the badge failing to *clear* after you've
  // read something — that reads as broken. So screens that mark messages read
  // call loadUnreadMessageCount() directly instead of waiting for the timer.
  const loadUnreadMessageCount = useCallback(async () => {
    try {
      const result = await messagesApi.unreadCount();
      if (result.success) {
        setUnreadMessages(Number(result.data?.count) || 0);
      }
    } catch {
      // best-effort
    }
  }, []);

  useEffect(() => {
    if (!user) {
      setUnreadMessages(0);
      return;
    }
    loadUnreadMessageCount();
    const interval = setInterval(loadUnreadMessageCount, 30000);
    return () => clearInterval(interval);
  }, [user, activeTab, loadUnreadMessageCount]);

  const value = {
    activeTab, setActiveTab,

    user, setUser,
    isFirstTimeUser, setIsFirstTimeUser,
    isInTutorialFlow, setIsInTutorialFlow,
    showProfileTutorial, setShowProfileTutorial,
    profileTutorialKey,

    selectedItem, setSelectedItem,
    selectedUser, setSelectedUser,
    selectedMessage, setSelectedMessage,
    deleteMessageCallback, setDeleteMessageCallback,
    selectedServiceProvider, setSelectedServiceProvider,

    sponsoredAdData, setSponsoredAdData,
    sponsoredAdSource, setSponsoredAdSource,

    isProfileReadOnly, setIsProfileReadOnly,
    personalProfileComplete, setPersonalProfileComplete,
    tradingProfileComplete, setTradingProfileComplete,
    openToPreferences, setOpenToPreferences,
    profileRefreshKey, setProfileRefreshKey,

    favorites, setFavorites,

    addItemType, setAddItemType,
    editItemData, setEditItemData,
    successData, setSuccessData,
    feedbackInitialData, setFeedbackInitialData,
    addAdvertisingKeyCounter, setAddAdvertisingKeyCounter,

    locationsScreenData, setLocationsScreenData,
    advertisementsScreenData, setAdvertisementsScreenData,

    tradingProfileInitialTab, setTradingProfileInitialTab,
    scrollToJobBoard, setScrollToJobBoard,
    scrollToServices, setScrollToServices,
    scrollToJobBoardSection, setScrollToJobBoardSection,
    jobsListRefreshKey, setJobsListRefreshKey,

    datePickerCallbackRef,
    selectedExpiryDate, setSelectedExpiryDate,
    paymentMethodView, setPaymentMethodView,

    pendingItemsFilter, setPendingItemsFilter,
    pendingItemsData, setPendingItemsData,

    showTrialBanner, setShowTrialBanner,
    shouldShowTrialBanner, dismissTrialBanner,
    showAddMenu, setShowAddMenu,
    showHamburgerMenu, setShowHamburgerMenu,
    showTerms, setShowTerms,
    showPrivacy, setShowPrivacy,
    showSignOutModal, setShowSignOutModal,
    alertModal, setAlertModal,
    previewProductionMode, setPreviewProductionMode, togglePreviewProductionMode,
    isDevEnvironment, showDevFeatures,

    unreadNotifications, unreadMessages, loadUnreadNotificationCount, adjustUnreadNotificationCount, loadUnreadMessageCount,
    myListUnseenCount, setMyListUnseenCount,

    servicesScreenFilters, setServicesScreenFilters,
    skillsNeededScreenFilters, setSkillsNeededScreenFilters,
    sponsoredListingsScreenFilters, setSponsoredListingsScreenFilters,
    cachedJobs, setCachedJobs,
    cachedSponsoredAds, setCachedSponsoredAds,
    cachedEvents, setCachedEvents,
    servicesScrollY, setServicesScrollY,

    trialDaysLeft,
  };

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}

export function useAppContext() {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error("useAppContext must be used within <AppProvider>");
  return ctx;
}


// Header controls can render outside the app provider in isolated previews.
export function usePreviewMode() {
  const ctx = useContext(AppContext);
  return {
    visible: Boolean(ctx?.isDevEnvironment && ctx?.user),
    production: Boolean(ctx?.previewProductionMode),
    toggle: ctx?.togglePreviewProductionMode,
  };
}
